import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../../components/Panel'
import { Metric } from '../../components/Metric'
import { BarList } from '../../components/charts/BarList'
import { visitorsApi, type Step } from '../../api/visitors'
import { fmtInt, PUBLICATION_NAMES } from '../../lib/format'
import { rate, STEP_LABEL } from '../../lib/visitorsFormat'
import { useVisitorsRange } from '../../lib/useVisitorsRange'
import { QueryProblem, Skeleton, VisitorsFrame, linkCls, tdn, th, thr } from './VisitorsFrame'

const STEPS: Step[] = ['visit', 'engaged', 'considered', 'checkout', 'paid']

export function VisitorsFunnelStep() {
  const { pub = '', step = '' } = useParams()
  const { range, search } = useVisitorsRange()
  const okPub = Object.hasOwn(PUBLICATION_NAMES, pub)
  const okStep = (STEPS as string[]).includes(step)
  const s = step as Step
  const q = useQuery({
    queryKey: ['visitors-funnel-step', pub, range.from, range.to, step],
    queryFn: () => visitorsApi.funnelStep(pub, range, s),
    enabled: okPub && okStep,
  })
  const base = `/visitors/${pub}`
  const label = okStep ? STEP_LABEL[s] : step
  const next = okStep && STEPS.indexOf(s) < STEPS.length - 1 ? STEPS[STEPS.indexOf(s) + 1] : null
  const nextLabel = next ? STEP_LABEL[next].toLowerCase() : ''
  const d = q.data

  return (
    <VisitorsFrame
      pub={pub}
      valid={okPub}
      title={label}
      crumbs={[{ label: 'Revenue funnel', to: `${base}?${search}` }, { label }]}
    >
      {!okStep ? (
        <Panel accent="red">
          <p className="text-paper">Unknown funnel step</p>
          <Link to={`${base}?${search}`} className={`${linkCls} font-mono text-[12px]`}>Back to the title page</Link>
        </Panel>
      ) : q.isError ? (
        <QueryProblem error={q.error} pub={pub} />
      ) : !d ? (
        <Skeleton />
      ) : (
        <>
          <Panel accent="amber">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <Metric size="lg" tone="amber" label={`${label} — visits`} value={d.sessions} />
              <Link
                to={`${base}/sessions?step=${s}&${search}`}
                className="min-h-[32px] px-3 py-2 font-mono text-[12px] border border-amber-glow text-amber-glow hover:bg-ink-700"
              >
                See the {fmtInt(d.sessions)} visits
              </Link>
            </div>
          </Panel>

          <Panel label="Where visits at this step came from" accent="cyan">
            {d.by_channel.length === 0 ? (
              <p className="font-mono text-[12px] text-paper-faint">No visits at this step in this range</p>
            ) : (
              <BarList
                rows={d.by_channel.map((c) => ({
                  label: c.key,
                  value: c.sessions,
                  display: next ? `${fmtInt(c.sessions)} → ${fmtInt(c.next_step_sessions)} reached ${nextLabel}` : fmtInt(c.sessions),
                  href: `${base}/sessions?step=${s}&channel=${encodeURIComponent(c.key)}&${search}`,
                }))}
              />
            )}
          </Panel>

          {(s === 'checkout' || s === 'paid') && (
            <>
              <Panel label="By checkout flow" accent="violet">
                <div className="overflow-x-auto">
                  <table className="w-full font-mono text-[12px] text-paper">
                    <thead>
                      <tr className="border-b border-ink-500">
                        <th className={th}>Flow</th>
                        {['Started', 'Reached payment', 'Paid', 'Completion'].map((h) => <th key={h} className={thr}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {d.by_flow.map((f) => (
                        <tr key={f.flow} className="border-b border-ink-500/50">
                          <td className="px-3 py-2">{f.flow}</td>
                          <td className={tdn}>{fmtInt(f.started)}</td>
                          <td className={tdn}>{fmtInt(f.reached_payment)}</td>
                          <td className={tdn}>{fmtInt(f.paid)}</td>
                          <td className={tdn}>{f.started > 0 ? rate(f.paid / f.started) : '—'}</td>
                        </tr>
                      ))}
                      {d.by_flow.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-paper-faint">No checkout flows in this range</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Panel>
              <Panel label="Fields left unfinished" accent="red">
                <div className="overflow-x-auto">
                  <table className="w-full font-mono text-[12px] text-paper">
                    <thead>
                      <tr className="border-b border-ink-500"><th className={th}>Field</th><th className={thr}>Visits</th></tr>
                    </thead>
                    <tbody>
                      {d.abandoned_fields.map((f) => (
                        <tr key={f.field} className="border-b border-ink-500/50">
                          <td className="px-3 py-2">{f.field}</td>
                          <td className={tdn}>{fmtInt(f.sessions)}</td>
                        </tr>
                      ))}
                      {d.abandoned_fields.length === 0 && <tr><td colSpan={2} className="px-3 py-4 text-paper-faint">No abandoned fields recorded</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
        </>
      )}
    </VisitorsFrame>
  )
}
